import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { logError } from "@/lib/logger";

/**
 * Hook to get the current profile version (updated_at timestamp)
 * Used as cache key for matching queries to ensure fresh data
 */
export const useProfileVersion = () => {
  return useQuery({
    queryKey: ["profile-version"],
    queryFn: async (): Promise<string | null> => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return null;

      // Use RPC call to get profile version
      const { data, error } = await supabase
        .rpc("get_profile_version", { p_user_id: session.user.id });

      if (error) {
        logError("[ProfileVersion] Error", error);
        return null;
      }

      return data as string | null;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
  });
};

/**
 * Get a short version hash from the profile updated_at timestamp
 * Used as part of the cache key for matching queries
 */
export const getProfileVersionHash = (updatedAt: string | null): string => {
  if (!updatedAt) return "default";
  
  // Convert to timestamp and take last 8 chars for a short hash
  const timestamp = new Date(updatedAt).getTime();
  return timestamp.toString(36);
};
